import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { HttpMethod, callApiWithOptions } from "src/services/apiService";
import styles from "./Consent.module.css";

// OIDC consent screen. The /oauth/authorize endpoint redirects the user here
// (with a pending-request id) the first time they link a given client. The
// grant is remembered server-side, so this is shown only once per app.
type ConsentInfo = {
  client: { clientId: string; displayName: string; logoUrl: string };
  user: { username: string; acctId: string };
  // Per-request CSRF token; echoed back on the decision POST. Server validates
  // it instead of the session csrf header — this page is entered via a
  // cross-origin redirect, so the Redux store isn't hydrated yet.
  csrf: string;
};

const Consent = () => {
  const [searchParams] = useSearchParams();
  const cid = searchParams.get("cid") ?? "";

  const [info, setInfo] = useState<ConsentInfo | null>(null);
  const [error, setError] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!cid) {
      setError("This linking request is missing or invalid.");
      return;
    }
    callApiWithOptions({ endpoint: `oauth/consent/${cid}`, method: HttpMethod.GET })
      .then(setInfo)
      .catch((e) =>
        setError(
          e?.data?.message ?? "This linking request has expired. Please start again.",
        ),
      );
  }, [cid]);

  const decide = async (approved: boolean) => {
    setSubmitting(true);
    try {
      const res = await callApiWithOptions({
        endpoint: `oauth/consent/${cid}/decision`,
        method: HttpMethod.POST,
        body: { approved, csrf: info?.csrf },
        redirect: false,
      });
      // Hand control back to the OAuth flow (redirect carries ?code or ?error).
      window.location.href = res.redirectTo;
    } catch (e: any) {
      setError(e?.message ?? "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  // Rendered inside LoginLayout's card (logo above, support footer below), so
  // there's no inner card here — content sits directly on that single surface.
  if (error) {
    return (
      <div className="text-center">
        <i
          className="bi bi-exclamation-triangle text-warning d-block mb-2"
          style={{ fontSize: "2rem", lineHeight: 1 }}
          aria-hidden="true"
        />
        <h4 className="mb-2">Linking unavailable</h4>
        <p className="text-muted mb-0">{error}</p>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="text-center">
      <i
        className="bi bi-link-45deg d-block mb-2"
        style={{ fontSize: "2.5rem", lineHeight: 1, color: "var(--primary-btn-color)" }}
        aria-hidden="true"
      />
      <h4 className="mb-2">Authorize {info.client.displayName}</h4>
      <p className="text-muted mb-4">
        <strong>{info.client.displayName}</strong> wants to link your DNCScrub account
        {info.user?.username ? ` (${info.user.username})` : ""} to your unified profile.
      </p>

      <div className="d-grid gap-2">
        <button
          type="button"
          className="btn btn-primary"
          disabled={submitting}
          onClick={() => decide(true)}
        >
          {submitting ? "Linking…" : "Authorize & link"}
        </button>
        <button
          type="button"
          className={`btn ${styles.cancelBtn}`}
          disabled={submitting}
          onClick={() => decide(false)}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default Consent;
